import * as next from '../../../types/next'
import { CONTROL_NAME } from '../../../utils/blocks/types'
import type * as current from '../../../utils/types/sharedTypes'
import type { UpBlockHandler } from '../types'
import {
  findBlockIdWithinStep,
  findBlockIdWithStepId,
  getBlockIdFromLogicSource,
  newId
} from '../utils'

import { upCommonSettings } from './common'

export const up: UpBlockHandler<next.ProductsBlock> = (
  uischema,
  _,
  journey
) => {
  if (uischema.type !== CONTROL_NAME.PRODUCT_SELECTION_CONTROL) {
    throw new Error('Expected a valid ProductSelection control')
  }

  const currentBlockId = uischema.id

  return {
    type: next.BLOCK_TYPE.PRODUCTS,
    settings: {
      ...upCommonSettings(uischema),
      ...upBlockSettings(uischema.options)
    },
    references: upReferences(uischema, journey, currentBlockId)
  }
}

export const upReferences = (
  uischema: current.BlockUiSchema,
  journey?: current.Journey,
  currentBlockId?: string
) => {
  if (!currentBlockId) {
    throw new Error(
      'Products block has no id, it is required to migrate product filters'
    )
  }
  const references: Record<next.ReferenceId, next.ProductBlockReference> = {}

  if (!journey) {
    return references
  }

  // Composite Price
  const componentMappings =
    uischema.options?.optionalPriceComponentMappings ?? []

  componentMappings.forEach((mapping: any) => {
    if (mapping.type === 'optional-price-component') {
      const blockId = findBlockIdWithStepId(
        journey,
        mapping.stepId,
        mapping.blockName
      )
      const referenceId = newId<next.ReferenceId>()

      references[referenceId] = {
        meaning: 'optional-price',
        referenceType: next.REFERENCED_TYPE.BLOCK,
        referenceId: blockId,
        id: referenceId,
        operator: 'EQUALS',
        value: mapping.value,
        metadata: {
          productId: mapping.metadata.productId,
          price: {
            priceId: mapping.metadata.priceId,
            componentId: mapping.metadata.priceComponentId
          }
        }
      } as next.OptionalPriceComponentReference
    } else {
      throw new Error('Invalid mapping type', mapping.type)
    }
  })

  // Variable Price
  const blockMappings = uischema.options?.blockMappings ?? []

  blockMappings.forEach((mapping: any) => {
    if (mapping.type === 'pricing') {
      const blockId = findBlockIdWithStepId(
        journey,
        mapping.stepId,
        mapping.blockName
      )
      const referenceId = newId<next.ReferenceId>()

      references[referenceId] = {
        meaning: 'variable-price',
        referenceType: next.REFERENCED_TYPE.BLOCK,
        referenceId: blockId,
        id: referenceId,
        metadata: {
          productId: mapping.metadata.productId,
          price: {
            priceId: mapping.metadata.priceId,
            componentId: mapping.metadata.priceComponentId
          }
        }
      } as next.VariablePriceReference
    } else {
      throw new Error('BlockMapping type not recognized', mapping.type)
    }
  })

  // Cross selling products
  const selectedBlocks = uischema.options?.selectedBlocks ?? []

  selectedBlocks.forEach((mapping: any) => {
    const blockId = findBlockIdWithinStep(
      journey,
      mapping.step.stepIndex,
      mapping.name
    )
    const referenceId = newId<next.ReferenceId>()

    references[referenceId] = {
      meaning: 'cross-selling-product',
      referenceType: next.REFERENCED_TYPE.BLOCK,
      referenceId: blockId,
      id: referenceId,
      products: mapping.selectedProducts,
      enabled: mapping.selected === undefined ? true : mapping.selected
    } as next.CrossSellingProductsReference
  })

  // Grid-fee address/availability reference
  const addressSettings = uischema.options?.injectAddressSettings

  // Address settings are enabled, but no related block is specified is not a valid case for a references
  if (addressSettings && addressSettings.relatedBlock) {
    const [stepId, blockName] = addressSettings.relatedBlock.split('/')
    const blockId = findBlockIdWithStepId(journey, stepId, blockName)
    const referenceId = newId<next.ReferenceId>()

    references[referenceId] = {
      meaning: 'grid-fee-area',
      referenceType: next.REFERENCED_TYPE.BLOCK,
      referenceId: blockId,
      id: referenceId
    } as next.GridFeeAreaReference
  }

  // Product filter
  const productFilters = uischema.options?.filters ?? []

  productFilters.forEach((filter: current.Filter) => {
    const referencedBlockId = getBlockIdFromLogicSource(
      '::InjFter::',
      currentBlockId,
      journey,
      filter.blockName
    )

    if (!referencedBlockId) {
      // When the block is not found, we skip creating the product filter reference
      // Inside a forEach return works as a continue, because why should code make any sense?
      return
    }

    const referenceId = newId<next.ReferenceId>()
    const filters: next.ProductFilter[] = []

    filter.options.forEach((option: current.FilterOption) => {
      const ids = option.ids

      ids.forEach((rawId: string) => {
        if (!rawId) {
          return
        }

        const [productId, priceId] = rawId.split('/')

        filters.push({
          productId,
          priceId,
          excludeOnValue: option.value.toString()
        } as next.ProductFilter)
      })
    })

    references[referenceId] = {
      meaning: 'product-filter',
      referenceType: next.REFERENCED_TYPE.BLOCK,
      referenceId: referencedBlockId,
      id: referenceId,
      filters: filters
    } as next.ProductFilterReference
  })

  return references
}

export const upBlockSettings = (options?: current.BlockUiSchema['options']) => {
  if (!options?.externalIntegration?.enabled) {
    // If the external integration is not enabled, we are using epilot catalog
    return {
      catalog: next.CATALOG.EPILOT,
      productsType: parseProductType(options?.productSelectionConfiguratorType),
      products: upProducts(options?.products),
      layout: upLayout(options),
      alignment: upAlignment(options),
      selection: upSelectionOption(
        options?.selectionType,
        options?.showQuantity
      )
    } as next.EpilotCatalogSettings
  } else {
    return {
      catalog: next.CATALOG.EXTERNAL,
      integrationId: options?.externalIntegration.integrationId,
      layout: upLayout(options),
      alignment: upAlignment(options),
      selection: upSelectionOption(
        options?.selectionType,
        options?.showQuantity
      )
    } as next.ExternalCatalogSettings
  }
}

const upSelectionOption = (
  selectionType: string,
  showQuantity: boolean
): next.SELECTION_OPTION => {
  if (selectionType !== 'many') {
    return next.SELECTION_OPTION.ONE
  }
  if (!showQuantity) {
    return next.SELECTION_OPTION.EACH_ONE
  }

  return next.SELECTION_OPTION.EACH_N
}

const upCtaOption = (ctaTextOption: string): next.CTA_OPTION => {
  if (ctaTextOption === 'cta_select') {
    return next.CTA_OPTION.SELECT
  }
  if (ctaTextOption === 'cta_subscribe') {
    return next.CTA_OPTION.SUBSCRIBE
  }
  if (ctaTextOption === 'cta_request_quote') {
    return next.CTA_OPTION.REQUEST_QUOTE
  }

  // Default to add to cart (includes cta_add_to_cart)
  return next.CTA_OPTION.ADD_TO_CART
}

const upAlignment = (
  options: current.BlockUiSchema['options']
): next.TILE_ALIGNMENT => {
  if (options?.justifyContent === 'flex-start') {
    return next.TILE_ALIGNMENT.LEFT
  }
  if (options?.justifyContent === 'space-between') {
    return next.TILE_ALIGNMENT.EVENLY_DISTRIBUTED
  }

  return next.TILE_ALIGNMENT.CENTER
}

const upLayout = (options: any): next.TileLayout => {
  let tileLayoutVariant = options?.tileLayoutVariant

  if (!tileLayoutVariant) {
    // When tile layout variant is not defined we use the default value
    tileLayoutVariant = defaultToVariant1()
  }

  const findPropertyInVariantOptions = (key: string) =>
    tileLayoutVariant.options.find((option: any) => option.key === key)

  const properties = {
    headline: findPropertyInVariantOptions('headline'),
    image: findPropertyInVariantOptions('image'),
    description: findPropertyInVariantOptions('description'),
    price: {
      ...findPropertyInVariantOptions('price'),
      showTrailingDecimalZeros: options?.showTrailingDecimalZeros || false,
      showRecurringTotal: options?.displayRecurringTotal || false,
      recurrenceUnit: options?.displayRecurringTotal
        ? options?.recurringTotalUnitOption
        : undefined,
      showUnitaryAverage: options?.displayUnitaryAverage || false,
      featurePriceAfterCashback: options?.spotlightPriceAfterCashback || false
    },
    priceComponents: {
      ...findPropertyInVariantOptions('price-components'),
      showTrailingDecimalZeros: options?.showTrailingDecimalZeros || false
    } as next.TileLayout['properties']['priceComponents'],
    cta: {
      ...findPropertyInVariantOptions('CTA'),
      option: upCtaOption(options?.ctaTextOption)
    },
    features: {
      ...findPropertyInVariantOptions('features'),
      maxCount: Number(options?.featuresLimit)
    },
    details: {
      ...findPropertyInVariantOptions('expand'),
      showMarketDataCharts: options?.showMarketDataCharts || false
    },
    files: findPropertyInVariantOptions('downloads')
  }

  return {
    variant: tileLayoutVariant.variant,
    properties: properties
  } as next.TileLayout
}

const upProducts = (products?: any[]): next.ProductProperties[] => {
  if (!products) {
    return []
  }

  return products.map((product) => ({
    productId: product.productId,
    priceId: product.priceId,
    isFeatured: product.isFeatured || false,
    featureAppearance: !product.isFeatured
      ? undefined
      : {
          labelText: product.featuredLabel,
          borderColor: product.featuredColor,
          labelColor: product.featuredLabelColor
        }
  }))
}

const parseProductType = (productSelectionConfiguratorType: string) => {
  if (productSelectionConfiguratorType === 'product_selector')
    return next.PRODUCTS_TYPE.STATIC
  if (productSelectionConfiguratorType === 'cross_sell_product_selector')
    return next.PRODUCTS_TYPE.CROSS_SELLING_ALL

  return next.PRODUCTS_TYPE.CROSS_SELLING_SELECTED
}

const defaultToVariant1 = () => {
  return {
    variant: '1',
    options: [
      {
        key: 'headline',
        isVisible: true,
        isConfigurable: false,
        order: 0
      },
      {
        key: 'image',
        isVisible: true,
        isConfigurable: true,
        order: 1
      },
      {
        key: 'description',
        isVisible: true,
        isConfigurable: true,
        order: 2
      },
      {
        key: 'price',
        isVisible: true,
        isConfigurable: false,
        order: 3
      },
      {
        key: 'price-components',
        isVisible: true,
        isConfigurable: true,
        order: 4
      },
      {
        key: 'CTA',
        isVisible: true,
        isConfigurable: false,
        order: 5
      },
      {
        key: 'features',
        isVisible: true,
        isConfigurable: true,
        order: 6
      },
      {
        key: 'expand',
        isVisible: true,
        isConfigurable: true,
        order: 7
      },
      {
        key: 'downloads',
        isVisible: true,
        isConfigurable: true,
        order: 8
      }
    ]
  }
}
